import { filterClasses } from "@swivl-mono/helpers";
import { Button, ButtonSize, ButtonVariant } from "@swivl-mono/ui";
import { AnimatePresence, motion } from "motion/react";
import { FC, memo } from "react";

import { FilterChip } from "./FilterChip";

enum Outlet {
  WRAPPER = "wrapper",
  CHIP = "chip",
  CLEAR_BUTTON = "clearButton"
}

export enum ActiveFilterChipSectionVariant {
  INLINE = "inline",
  MULTI_LINE = "multiLine"
}

interface BaseActiveFilterChipSectionProps {
  classNames?: { [Key in Outlet]?: string };
  filterOptions: {
    label: string;
    value: string;
    isMultiSelect?: boolean;
    options?: { label: string; value: string }[];
  }[];
  selectedFiltersByGroup: Record<string, string[]>;
  handleClearFilter: (groupKey: string, value: string) => void;
  handleClearFilters: () => void;
  handleSetFilters?: (groupKey: string, filters: string[]) => void;
  variant?: ActiveFilterChipSectionVariant;
}

const BaseActiveFilterChipSection: FC<BaseActiveFilterChipSectionProps> = ({
  classNames,
  filterOptions,
  selectedFiltersByGroup,
  handleClearFilter,
  handleClearFilters,
  handleSetFilters,
  variant = ActiveFilterChipSectionVariant.MULTI_LINE
}) => {
  const hasSelectedFilters = Object.values(selectedFiltersByGroup).some((selectedFilters) => selectedFilters && selectedFilters.length > 0);

  const handleClearGroup = (groupValue: string) => {
    handleClearFilter(groupValue, ""); // clearing the entire group
  };

  return (
    <AnimatePresence mode="wait">
      {hasSelectedFilters && (
        <motion.div
          className={filterClasses(classNames?.[Outlet.WRAPPER], "w-full bg-white pb-px")}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          style={{ overflow: "hidden" }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          <div
            className={filterClasses(
              "flex justify-between px-3 py-2",
              variant === ActiveFilterChipSectionVariant.MULTI_LINE ? "flex-col gap-1" : "items-center gap-4"
            )}
          >
            {variant === ActiveFilterChipSectionVariant.MULTI_LINE && (
              <div className="flex w-full items-center justify-between">
                <span className="body-mono-alt text-grey-600">Filtered by</span>
                <Button
                  className={classNames?.[Outlet.CLEAR_BUTTON]}
                  variant={ButtonVariant.GHOST}
                  onClick={handleClearFilters}
                  size={ButtonSize.SMALL}
                >
                  Clear All
                </Button>
              </div>
            )}
            <div className="items-left flex flex-wrap gap-2">
              {variant === ActiveFilterChipSectionVariant.INLINE && (
                <div className="mr-2 flex items-center">
                  <span className="body-mono-alt text-grey-600">Filtered by</span>
                </div>
              )}
              {filterOptions.map((group) => {
                const selectedFilters = selectedFiltersByGroup[group.value] ?? [];
                if (selectedFilters.length === 0 || !group.options) return null;

                const matchedLabels = group.options.filter((opt) => selectedFilters.includes(opt.value)).map((opt) => opt.label);
                // fall back to raw param values when options have not loaded or formats differ
                const labels = matchedLabels.length > 0 ? matchedLabels : selectedFilters;

                return (
                  <FilterChip
                    key={group.value}
                    className={classNames?.[Outlet.CHIP]}
                    groupLabel={group.label}
                    groupValue={group.value}
                    labels={labels}
                    selectedFilters={selectedFilters}
                    options={group.options}
                    isMultiSelect={group.isMultiSelect !== false}
                    onClearGroup={() => handleClearGroup(group.value)}
                    onSetFilters={handleSetFilters}
                  />
                );
              })}
            </div>
            {variant === ActiveFilterChipSectionVariant.INLINE && (
              <Button
                className={classNames?.[Outlet.CLEAR_BUTTON]}
                variant={ButtonVariant.GHOST}
                onClick={handleClearFilters}
                size={ButtonSize.SMALL}
              >
                Clear
              </Button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const ActiveFilterChipSection = memo(BaseActiveFilterChipSection);
